import { motion } from "framer-motion";

export default function Experience() {
  return (
    <motion.section
      id="experience"
      className="min-h-screen px-6 py-20 bg-white dark:bg-gray-900 text-gray-800 dark:text-white"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
    >
      <h2 className="text-5xl font-extrabold mb-6 border-b-4 border-blue-600 inline-block">
        Experience
      </h2>

      <div className="space-y-12 mt-4">
        {/* Beekley Medical */}
        <div>
          <div className="flex justify-between items-start gap-4">
            <div>
              <h3 className="text-3xl font-bold text-blue-800 dark:text-blue-300">
                Beekley Medical — Bristol, CT
              </h3>
              <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
                Senior Systems Administrator
              </p>
            </div>
            <span className="text-base text-gray-700 dark:text-gray-200 whitespace-nowrap">
              Jan 2024 – Present
            </span>
          </div>
          <ul className="list-disc list-inside mt-3 text-lg text-gray-700 dark:text-gray-300 space-y-1">
            <li>Design and administer enterprise storage and virtualization across VMware vSphere and Nutanix AHV, ensuring high availability, scalability, and 99.9%+ uptime.</li>
            <li>Manage full infrastructure lifecycle with Infrastructure as Code — Terraform for provisioning and Ansible for configuration management, patching, and compliance enforcement.</li>
            <li>Administer Windows Server (AD, DNS, DHCP, Group Policy) and Linux (RHEL, Ubuntu, CentOS) with CIS-aligned hardening and standardized configuration baselines.</li>
            <li>Own OS patching lifecycle across 100+ endpoints — patch scheduling, verification, and vulnerability remediation with zero missed SLA windows.</li>
            <li>Implement backup and disaster recovery with Rubrik against defined RTO/RPO targets, including scheduled restore validation; achieve ~2× faster restorations.</li>
            <li>Maintain centralized monitoring and logging (Zabbix, Prometheus/Grafana) for traceability of system and configuration changes.</li>
            <li>Manage SSL/TLS certificate lifecycle and enforce least-privilege access and key-based SSH authentication.</li>
            <li>Lead incident response and root-cause analysis through ServiceNow, reducing downtime by ~40%.</li>
            <li>Author SOPs, runbooks, and architectural diagrams; deliver IT training to 50+ users, cutting ticket volume by ~25%.</li>
          </ul>
        </div>

        {/* TivoTechnology LLC */}
        <div>
          <div className="flex justify-between items-start gap-4">
            <div>
              <h3 className="text-3xl font-bold text-blue-800 dark:text-blue-300">
                TivoTechnology LLC — Hartford, CT
              </h3>
              <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
                Systems and Cloud Administrator
              </p>
            </div>
            <span className="text-base text-gray-700 dark:text-gray-200 whitespace-nowrap">
              Dec 2022 – Jan 2025
            </span>
          </div>
          <ul className="list-disc list-inside mt-3 text-lg text-gray-700 dark:text-gray-300 space-y-1">
            <li>Built and maintained Infrastructure as Code with Terraform and Ansible for automated provisioning of AWS and Azure compute, networking, and identity resources — cutting deployment time by ~50% and enforcing standardized, auditable builds.</li>
            <li>Used GitHub for version control, peer review, and CI/CD-driven deployments of infrastructure changes.</li>
            <li>Maintained 20+ physical and virtual Linux servers (RHEL, Ubuntu, Debian) across 10+ client environments — patch scheduling, compliance reporting, and automated maintenance via Bash and PowerShell.</li>
            <li>Managed 50+ VMware VMs with resource optimization and scheduled template refreshes — zero unplanned outages over 12 months.</li>
            <li>Administered Active Directory for 300+ users in hybrid on-prem/cloud environments with policy enforcement and access controls.</li>
            <li>Designed and implemented backup/DR strategies reducing data-loss exposure by ~90% across the client portfolio.</li>
            <li>Installed and configured 40+ network devices (Cisco switches, Fortinet firewalls, WAPs); resolved ~99% of LAN/WAN and firewall issues within SLA.</li>
            <li>Acted as technical liaison between clients and engineering teams, translating requirements into infrastructure solutions for non-technical stakeholders.</li>
          </ul>
        </div>

        {/* Baystate Medical Center (Pixel Health) */}
        <div>
          <div className="flex justify-between items-start gap-4">
            <div>
              <h3 className="text-3xl font-bold text-blue-800 dark:text-blue-300">
                Baystate Medical Center (Pixel Health) — Springfield, MA
              </h3>
              <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
                IT Field Services Technician
              </p>
            </div>
            <span className="text-base text-gray-700 dark:text-gray-200 whitespace-nowrap">
              Aug 2023 – Jan 2024
            </span>
          </div>
          <ul className="list-disc list-inside mt-3 text-lg text-gray-700 dark:text-gray-300 space-y-1">
            <li>Maintained VMware Horizon virtual desktop infrastructure for 80+ clinical users — session availability, performance monitoring, and profile troubleshooting.</li>
            <li>Deployed 100+ endpoint devices with zero post-installation defects.</li>
            <li>Delivered remote network support via Infoblox, reducing troubleshooting time by ~40% and connectivity downtime by ~50%.</li>
            <li>Onboarded 30+ clinical users with full IT orientation; documented procedures aligned with hospital change management standards.</li>
          </ul>
        </div>

        {/* Hartford HealthCare (TEKsystems) */}
        <div>
          <div className="flex justify-between items-start gap-4">
            <div>
              <h3 className="text-3xl font-bold text-blue-800 dark:text-blue-300">
                Hartford HealthCare (TEKsystems) — Hartford, CT
              </h3>
              <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
                IT Field Services Technician
              </p>
            </div>
            <span className="text-base text-gray-700 dark:text-gray-200 whitespace-nowrap">
              Jan 2023 – Aug 2023
            </span>
          </div>
          <ul className="list-disc list-inside mt-3 text-lg text-gray-700 dark:text-gray-300 space-y-1">
            <li>Resolved ~99% of ServiceNow/BMC Remedy tickets within SLA across a large hospital network.</li>
            <li>Administered Active Directory accounts, Group Policy, and permissions for 400+ Microsoft 365 users with zero errors.</li>
            <li>Installed and configured 120+ desktop systems across departments with full adherence to security and change management standards.</li>
          </ul>
        </div>

        {/* D&D Supermarket */}
        <div>
          <div className="flex justify-between items-start gap-4">
            <div>
              <h3 className="text-3xl font-bold text-blue-800 dark:text-blue-300">
                D&amp;D Supermarket — Hartford, CT
              </h3>
              <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
                IT Technician
              </p>
            </div>
            <span className="text-base text-gray-700 dark:text-gray-200 whitespace-nowrap">
              May 2018 – Dec 2022
            </span>
          </div>
          <ul className="list-disc list-inside mt-3 text-lg text-gray-700 dark:text-gray-300 space-y-1">
            <li>Resolved ~85% of incidents within 1 hour; maintained POS network connectivity and implemented backup solutions.</li>
            <li>Performed hardware upgrades, increasing device lifespan by ~35%.</li>
            <li>Documented IT procedures and infrastructure inventory, improving team efficiency and knowledge retention.</li>
          </ul>
        </div>
      </div>
    </motion.section>
  );
}
